/* --- File: src/pages/LoginPage.js (đã bỏ tính năng đăng nhập bằng khuôn mặt) --- */
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Form, Input, Button, Spin,
  App, Modal
} from 'antd';

import '../styles/loginPage.css'; 
import { MailOutlined, LockOutlined, LoginOutlined, SafetyCertificateOutlined } from '@ant-design/icons';

import { auth } from '../firebaseConfig'; 
import { signInWithEmailAndPassword } from 'firebase/auth'; 
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useAuth } from '../context/UserContext'; 
import AiLogo from '../components/AiLogo'; 



function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { userData, loading: authLoading } = useAuth();
  
  const { message: messageApi } = App.useApp(); 
  const [loading, setLoading] = useState(false); 
  const [isForgotOpen, setIsForgotOpen] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [forgotForm] = Form.useForm();

  useEffect(() => {
    if (!authLoading && userData) {
      // Nếu người dùng đang định vào 1 đường dẫn cụ thể (VD: link bài tập được
      // gửi qua Zalo/tin nhắn) trước khi bị chuyển sang đăng nhập, quay lại
      // đúng chỗ đó sau khi đăng nhập xong — thay vì luôn về trang chủ.
      const redirectTo = location.state?.from && location.state.from !== '/login' ? location.state.from : '/';
      navigate(redirectTo, { replace: true }); 
    }
  }, [userData, authLoading, navigate, location.state]); 

  const onFinish = async (values) => {
    const { email, password } = values;
    setLoading(true);
    try {
      await signInWithEmailAndPassword(auth, email, password);
      messageApi.success('Đăng nhập thành công! Đang chuyển hướng...');
    } catch (error) {
      console.error('Lỗi đăng nhập:', error.code);
      if (error.code === 'auth/invalid-credential' || error.code === 'auth/wrong-password' || error.code === 'auth/user-not-found') {
        messageApi.error('Sai email hoặc mật khẩu. Vui lòng thử lại!');
      } else {
        messageApi.error('Đã xảy ra lỗi kết nối. Vui lòng thử lại sau.');
      }
    } finally {
      setLoading(false); 
    }
  };

  const handleForgotPassword = async () => {
    try {
      const values = await forgotForm.validateFields();
      setResetLoading(true);

      // Cloud Function tự lo hết: tìm tài khoản theo email đăng nhập, lấy Email
      // cá nhân, tạo link đặt lại mật khẩu, và gửi email luôn (qua Gmail SMTP) —
      // web không cần đụng tới dịch vụ gửi email nào cả.
      const functionsInstance = getFunctions();
      const sendReset = httpsCallable(functionsInstance, 'sendPasswordResetToPersonalEmail');
      await sendReset({ loginEmail: values.email });

      messageApi.success('Đã gửi liên kết đặt lại mật khẩu tới email cá nhân của bạn!');
      setIsForgotOpen(false);
      forgotForm.resetFields();
    } catch (error) {
      if (error?.errorFields) return; // lỗi validate form — antd đã hiện sẵn
      // Không tiết lộ tài khoản có tồn tại hay không (bảo mật) — luôn báo chung chung
      console.error('Lỗi gửi email đặt lại mật khẩu:', error.code || error.message);
      messageApi.info('Nếu tài khoản này tồn tại và có Email cá nhân, một liên kết đặt lại mật khẩu đã được gửi tới đó.');
      setIsForgotOpen(false);
      forgotForm.resetFields();
    } finally {
      setResetLoading(false);
    }
  };

  if (authLoading) return <main className="login-page login-loading"><Spin size="large" /><p>Đang khởi động hệ thống…</p></main>;

  return (
    <main className="login-page">
      <header className="login-brand"><span className="login-brand-mark">c&#125;d</span><span>Coder’s Den <small>EDUTRACK</small></span></header>
      <div className="login-shell">
        <section className="login-intro">
          <span className="login-eyebrow">KHÔNG GIAN HỌC TẬP CỦA BẠN</span>
          <h1>Mỗi ngày học.<br /><em>Một bước tiến.</em></h1>
          <p>Theo dõi lớp học, hoàn thành bài tập và nhìn lại hành trình tiến bộ — tất cả trong một không gian.</p>
          <div className="login-illustration" aria-hidden="true">
            <div className="login-mini-heading"><span>Hành trình học tập</span><span></span></div>
            <div className="login-step"><b>01</b><span>Khám phá kiến thức<small>Bài học & tài liệu</small></span><i>✓</i></div>
            <div className="login-step"><b>02</b><span>Thực hành mỗi ngày<small>Bài tập & dự án</small></span><i>✓</i></div>
            <div className="login-step"><b>03</b><span>Nhìn thấy tiến bộ<small>Kết quả & nhận xét</small></span><i></i></div>
          </div>
        </section>
        <section className="login-card" aria-labelledby="login-title">
          <div className="login-logo"><AiLogo /></div>
          <h2 id="login-title">Chào mừng trở lại</h2>
          <p className="login-subtitle">Đăng nhập để tiếp tục hành trình của bạn.</p>
          <Form name="login_form" onFinish={onFinish} layout="vertical" size="large" requiredMark={false}>
            <Form.Item name="email" label="Email đăng nhập" rules={[{required:true,message:'Vui lòng nhập email.'},{type:'email',message:'Email không hợp lệ.'}]}>
              <Input prefix={<MailOutlined />} autoComplete="username" inputMode="email" placeholder="Email được cấp bởi trung tâm" />
            </Form.Item>
            <Form.Item name="password" label="Mật khẩu" rules={[{required:true,message:'Vui lòng nhập mật khẩu.'}]}>
              <Input.Password prefix={<LockOutlined />} autoComplete="current-password" placeholder="Nhập mật khẩu của bạn" />
            </Form.Item>
            <div className="login-forgot"><Button type="link" onClick={()=>setIsForgotOpen(true)}>Quên mật khẩu?</Button></div>
            <Button type="primary" htmlType="submit" block loading={loading} icon={<LoginOutlined />} className="login-submit">Đăng nhập</Button>
          </Form>
          <div className="login-help"><SafetyCertificateOutlined /><div><strong>Bạn chưa có tài khoản?</strong><p>Liên hệ giáo viên hoặc giáo vụ để được cấp tài khoản truy cập.</p></div></div>
        </section>
      </div>
      <footer className="login-footer">{new Date().getFullYear()} Coder’s Den · EduTrack</footer>
      <Modal open={isForgotOpen} onCancel={()=>{setIsForgotOpen(false);forgotForm.resetFields();}} footer={null} centered width={440} title="Đặt lại mật khẩu" className="login-reset">
        <p>Nhập email đăng nhập. Liên kết đặt lại mật khẩu sẽ được gửi tới email cá nhân đã đăng ký của bạn.</p>
        <Form form={forgotForm} layout="vertical" onFinish={handleForgotPassword} requiredMark={false}>
          <Form.Item name="email" label="Email đăng nhập" rules={[{required:true,message:'Vui lòng nhập email.'},{type:'email',message:'Email không hợp lệ.'}]}>
            <Input size="large" prefix={<MailOutlined />} autoComplete="username" placeholder="Nhập email đăng nhập" />
          </Form.Item>
          <Button type="primary" block htmlType="submit" loading={resetLoading}>Gửi liên kết đặt lại mật khẩu</Button>
        </Form>
      </Modal>
    </main>
  );
}

export default LoginPage;
